// =====================================================
// ADMIN — REFER & EARN MANAGEMENT  (STEP 19 → STEP 24)
//
//   Overview    STEP 19 — every counter the task lists
//   Referrals   STEP 19/20 — the filterable queue, and one referral in full
//   Fraud       STEP 23 — everything held Under Review, with its reasons
//   Payouts     STEP 21/22 — the withdrawal queue, payout details, Pay Reward
//   Settings    reward amount, qualifying time, eligibility conditions (Add
//               Condition), and the audit trail of every change
//
// Nothing is decided here. Approve, reject and pay all POST to the server,
// which owns the state machine and the money — this screen only shows what the
// server reports and sends the admin's decision.
// =====================================================

import { useCallback, useEffect, useState } from "react";
import Navbar from "../Navbar";
import "./AdminReferEarn.css";
import {
  adminReferral,
  formatClock,
  formatDateTime,
  formatRemaining,
  statusMeta,
  conditionStatusMeta,
  ELIGIBILITY_META,
} from "../../services/referralApi";

function AdminReferEarn() {
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("referrals");
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "info") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadOverview = useCallback(() => {
    adminReferral
      .overview()
      .then(setOverview)
      .catch(() => showToast("Could not load the overview.", "error"));
  }, []);

  useEffect(loadOverview, [loadOverview]);

  return (
    <div className="ar-wrapper">
      <Navbar />
      <div className="ar-container">
        <header className="ar-head">
          <h1 className="ar-title">Refer &amp; Earn Management</h1>
          <p className="ar-sub">
            Every referral, its anti-fraud verdict and its payout — one screen.
          </p>
        </header>

        {/* ===== STEP 19 — OVERVIEW ===== */}
        {overview && (
          <>
            <section className="ar-stats">
              <Tile label="Total Referrals" value={overview.totalReferrals} />
              <Tile label="New Today" value={overview.newReferralsToday} tone="accent" />
              <Tile label="Pending" value={overview.pendingReferrals} tone="warn" />
              <Tile label="Completed" value={overview.completedReferrals} tone="good" />
              <Tile label="Expired" value={overview.expiredReferrals} tone="bad" />
              <Tile label="Under Review" value={overview.underReviewReferrals} tone="review" />
              <Tile label="Rejected" value={overview.rejectedReferrals} tone="muted" />
            </section>

            <section className="ar-stats">
              <Tile label="Pending Rewards" value={`₹${overview.pendingRewards}`} tone="warn" />
              <Tile label="Approved Rewards" value={`₹${overview.approvedRewards}`} tone="good" />
              <Tile label="Paid Out" value={`₹${overview.paidRewards}`} tone="accent" />
              <Tile label="Total Payout Cost" value={`₹${overview.totalPayoutCost}`} tone="accent" />
            </section>

            {/* The live, admin-configured programme — edited on the Settings tab. */}
            <section className="ar-stats">
              <Tile label="Reward per Referral" value={`₹${overview.rewardAmount}`} tone="good" />
              <Tile label="Qualifying Time" value={`${overview.requiredUsageMin} min`} tone="accent" />
              <Tile
                label="Notifications Off (unpaid)"
                value={overview.notificationFailing ?? 0}
                tone={overview.notificationFailing ? "bad" : "muted"}
              />
            </section>
          </>
        )}

        {/* ===== TABS ===== */}
        <nav className="ar-tabs" role="tablist">
          {[
            ["referrals", "Referrals"],
            ["fraud", `Fraud Queue${overview?.underReviewReferrals ? ` (${overview.underReviewReferrals})` : ""}`],
            ["payouts", "Payouts"],
            ["settings", "⚙️ Settings"],
          ].map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              className={`ar-tab ${tab === key ? "is-active" : ""}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </nav>

        {tab === "referrals" && (
          <ReferralsTab showToast={showToast} onChanged={loadOverview} />
        )}
        {tab === "fraud" && <FraudTab showToast={showToast} onChanged={loadOverview} />}
        {tab === "payouts" && <PayoutsTab showToast={showToast} onChanged={loadOverview} />}
        {tab === "settings" && <SettingsTab showToast={showToast} onChanged={loadOverview} />}
      </div>

      {toast && <div className={`ar-toast ar-toast-${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

function Tile({ label, value, tone = "neutral" }) {
  return (
    <div className={`ar-tile ar-tile-${tone}`}>
      <span className="ar-tile-value">{value}</span>
      <span className="ar-tile-label">{label}</span>
    </div>
  );
}

// =====================================================
// STEP 19 / STEP 20 — REFERRALS
// =====================================================

function ReferralsTab({ showToast, onChanged }) {
  const [filters, setFilters] = useState({ status: "", search: "", eligibility: "", notification: "" });
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    adminReferral
      .list({ ...filters, page, limit: 25 })
      .then(setData)
      .catch(() => showToast("Could not load referrals.", "error"));
    // showToast is stable enough for this screen; re-creating `load` on every
    // render would restart the fetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  useEffect(load, [load]);

  return (
    <>
      <div className="ar-filters">
        <input
          placeholder="Search by name or email…"
          value={filters.search}
          onChange={(e) => {
            setPage(1);
            setFilters((f) => ({ ...f, search: e.target.value }));
          }}
        />
        <select
          value={filters.status}
          onChange={(e) => {
            setPage(1);
            setFilters((f) => ({ ...f, status: e.target.value }));
          }}
        >
          <option value="">All statuses</option>
          <option value="pending">🟡 Pending</option>
          <option value="in_progress">🔵 In Progress</option>
          <option value="completed">🟢 Completed</option>
          <option value="expired">🔴 Expired</option>
          <option value="under_review">⚠️ Under Review</option>
          <option value="rejected">❌ Rejected</option>
        </select>
        {/* "Why isn't this payable?" — the condition engine's verdict. */}
        <select
          value={filters.eligibility}
          aria-label="Eligibility"
          onChange={(e) => {
            setPage(1);
            setFilters((f) => ({ ...f, eligibility: e.target.value }));
          }}
        >
          <option value="">Any eligibility</option>
          <option value="eligible">Eligible</option>
          <option value="pending">Pending</option>
          <option value="ineligible">Ineligible</option>
        </select>
        <select
          value={filters.notification}
          aria-label="Notification permission"
          onChange={(e) => {
            setPage(1);
            setFilters((f) => ({ ...f, notification: e.target.value }));
          }}
        >
          <option value="">Any notification state</option>
          <option value="failing">🔕 Notifications not allowed</option>
        </select>
      </div>

      {!data ? (
        <div className="ar-loading">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="ar-empty">No referrals match this filter.</div>
      ) : (
        <div className="ar-rows">
          {data.items.map((r) => (
            <ReferralRow key={r.id} r={r} onOpen={() => setOpenId(r.id)} />
          ))}
        </div>
      )}

      {data && data.totalPages > 1 && (
        <Pager page={data.page} totalPages={data.totalPages} onPage={setPage} />
      )}

      {openId && (
        <ReferralDetail
          id={openId}
          onClose={() => setOpenId(null)}
          onActed={() => {
            setOpenId(null);
            load();
            onChanged();
          }}
          showToast={showToast}
        />
      )}
    </>
  );
}

function ReferralRow({ r, onOpen }) {
  const meta = statusMeta(r.status);
  return (
    <button className="ar-row" onClick={onOpen}>
      <div className="ar-row-main">
        <strong>{r.referredUser?.name || "(deleted)"}</strong>
        <span className="ar-row-arrow">← referred by</span>
        <strong>{r.referrer?.name || "(deleted)"}</strong>
      </div>
      <div className="ar-row-ticks">
        <span title="Install">{r.install ? "✅" : "⏳"}</span>
        <span title="Blog">{r.blog ? "✅" : "⏳"}</span>
        <span title="Usage requirement">
          {r.usage ? "✅" : `⏳ ${formatClock(r.usageMs)}`}
        </span>
        {r.notification && (
          <span title={`Notifications: ${r.notification.label}`}>
            {r.notification.state === "granted" ? "🔔" : "🔕"}
          </span>
        )}
      </div>
      {/* The plain-language verdict: Eligible / Pending / Ineligible and why. */}
      {r.eligibility && <EligibilityLine e={r.eligibility} />}
      <div className="ar-row-meta">
        <span className="ar-chip" style={{ "--chip": meta.color }}>
          {meta.icon} {meta.label}
        </span>
        {/* A still-running referral shows how long it has left — the single
            most useful thing about it at a glance. */}
        {(r.status === "pending" || r.status === "in_progress") && (
          <span className="ar-muted">{formatRemaining(r.remainingMs)} left</span>
        )}
        <span className="ar-row-amount">
          ₹{r.reward.amount}
          {r.reward.creditedAt ? " · credited" : ""}
        </span>
      </div>
    </button>
  );
}

function EligibilityLine({ e }) {
  const meta = ELIGIBILITY_META[e.status] || { label: e.label, color: "#94a3b8" };
  return (
    // A span, not a <p>: it also renders inside the row's <button>.
    <span className="ar-elig">
      <strong style={{ color: meta.color }}>{e.label || meta.label}</strong>
      <span> — {e.reason}</span>
    </span>
  );
}

// Push-check verdicts, worded for the admin.
const PUSH_LABELS = {
  valid: "Firebase accepted this user's push registration",
  invalid: "Firebase rejected every push registration (permission likely revoked or app removed)",
  none: "No push token registered — cannot cross-check",
  unavailable: "Push check unavailable — browser report only",
  unchecked: "Not checked yet",
};

// STEP 20 — one referral, in full.
function ReferralDetail({ id, onClose, onActed, showToast }) {
  const [d, setD] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminReferral
      .detail(id)
      .then(setD)
      .catch(() => showToast("Could not load the referral.", "error"));
  }, [id, showToast]);

  const act = async (action) => {
    setBusy(true);
    try {
      const res = await adminReferral.review(id, action, reason);
      showToast(res.message, "success");
      onActed();
    } catch (err) {
      showToast(err.response?.data?.message || "Could not record the decision.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Referral Details">
      {!d ? (
        <div className="ar-loading">Loading…</div>
      ) : (
        <>
          <dl className="ar-kv">
            <div><dt>Referrer</dt><dd>{d.referrer?.name} · {d.referrer?.email}</dd></div>
            <div><dt>Referred User</dt><dd>{d.referredUser?.name} · {d.referredUser?.email}</dd></div>
            <div><dt>Joined</dt><dd>{formatDateTime(d.joinedAt)}</dd></div>
            <div><dt>Deadline</dt><dd>{formatDateTime(d.deadlineAt)}</dd></div>
            <div><dt>Code / Path</dt><dd>{d.referralCode} · via {d.source}</dd></div>
            <div>
              <dt>Security band</dt>
              <dd>{d.referredUser?.fraudBand} ({d.referredUser?.fraudScore})</dd>
            </div>
            <div>
              <dt>Install / first open</dt>
              <dd>
                {d.conditions.install.done
                  ? `${formatDateTime(d.conditions.install.at)} (${d.conditions.install.source})`
                  : "No install signal yet"}
              </dd>
            </div>
          </dl>

          {/* ===== ELIGIBILITY — the condition engine, re-run when opened ===== */}
          {d.eligibility && (
            <>
              <h4 className="ar-section-title">Eligibility</h4>
              <EligibilityLine e={d.eligibility} />
              <p className="ar-muted">Checked {formatDateTime(d.eligibility.evaluatedAt)}</p>
              <ul className="ar-elig-list">
                {d.eligibility.conditions.map((c) => {
                  const m = conditionStatusMeta(c.status);
                  return (
                    <li key={c.key} className={`ar-elig-item is-${c.status}`}>
                      <span className="ar-elig-mark" aria-hidden="true">{m.icon}</span>
                      <div>
                        <strong>{c.name}</strong>
                        {!c.required && <em className="ar-muted"> (optional)</em>}
                        <span className="ar-elig-status" style={{ color: m.color }}> · {m.label}</span>
                        <span className="ar-elig-detail">
                          {c.detail}
                          {c.at && ` (${formatDateTime(c.at)})`}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {/* ===== NOTIFICATION PERMISSION ===== */}
          {d.notification && (
            <>
              <h4 className="ar-section-title">Notification Permission</h4>
              <dl className="ar-kv">
                <div><dt>Current state</dt><dd>{d.notification.label}</dd></div>
                <div><dt>Last reported</dt><dd>{formatDateTime(d.notification.lastReportedAt)}</dd></div>
                <div><dt>First allowed</dt><dd>{formatDateTime(d.notification.firstGrantedAt)}</dd></div>
                <div><dt>Last turned off</dt><dd>{formatDateTime(d.notification.lastRevokedAt)}</dd></div>
                <div>
                  <dt>Server push check</dt>
                  <dd>
                    {PUSH_LABELS[d.notification.push?.status] || d.notification.push?.status}
                    {d.notification.push?.checkedAt && ` · ${formatDateTime(d.notification.push.checkedAt)}`}
                  </dd>
                </div>
              </dl>
              {d.notification.changes?.length > 0 && (
                <ul className="ar-history">
                  {d.notification.changes.map((ch, i) => (
                    <li key={i}>
                      <span>{ch.from || "—"} → <strong>{ch.state}</strong></span>
                      <span className="ar-muted">{formatDateTime(ch.at)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="ar-muted ar-hint">{d.notification.limitation}</p>
            </>
          )}

          <h4 className="ar-section-title">Conditions</h4>
          <ol className="ar-conditions">
            <li className={d.conditions.install.done ? "is-done" : ""}>
              <span>{d.conditions.install.done ? "✅" : "⏳"}</span>
              <span>Install — {d.conditions.install.done
                ? `${formatDateTime(d.conditions.install.at)} (${d.conditions.install.source})`
                : "not yet"}</span>
            </li>
            <li className={d.conditions.blog.done ? "is-done" : ""}>
              <span>{d.conditions.blog.done ? "✅" : "⏳"}</span>
              <span>
                Blog Upload — {d.conditions.blog.done ? formatDateTime(d.conditions.blog.at) : "not yet"}
                {d.conditions.blog.blog && (
                  <em className="ar-blog">
                    {" "}“{d.conditions.blog.blog.title}”
                    {d.conditions.blog.blog.spam && <strong className="ar-flag"> flagged spam</strong>}
                  </em>
                )}
              </span>
            </li>
            <li className={d.conditions.usage.done ? "is-done" : ""}>
              <span>{d.conditions.usage.done ? "✅" : "⏳"}</span>
              <span>
                Usage — {formatClock(d.conditions.usage.activeMs)} /{" "}
                {formatClock(d.conditions.usage.requiredMs)}
                {d.conditions.usage.done && ` at ${formatDateTime(d.conditions.usage.at)}`}
                {/* The beat count is the evidence behind the fake-usage check:
                    the full requirement credited over very few beats did not
                    happen in real time. */}
                <em className="ar-beats">
                  {" "}· {d.conditions.usage.beats} activity beats (min {d.conditions.usage.minBeats})
                </em>
              </span>
            </li>
          </ol>

          <h4 className="ar-section-title">Anti-Fraud</h4>
          {!d.fraud.checked ? (
            <p className="ar-muted">Not run yet — the conditions are not complete.</p>
          ) : d.fraud.passed ? (
            <p className="ar-pass">✅ Passed — {formatDateTime(d.fraud.checkedAt)}</p>
          ) : (
            <ul className="ar-reasons">
              {d.fraud.reasons.map((r, i) => (
                <li key={i}>
                  <strong>{r.label}</strong>
                  <span>{r.detail}</span>
                </li>
              ))}
            </ul>
          )}

          <h4 className="ar-section-title">Reward</h4>
          <dl className="ar-kv">
            <div>
              <dt>Amount</dt>
              <dd>
                ₹{d.reward.amount}
                {d.reward.amountLockedAt
                  ? ` · locked ${formatDateTime(d.reward.amountLockedAt)}`
                  : ` · follows current reward (₹${d.currentRewardAmount}) until eligible`}
              </dd>
            </div>
            <div><dt>Reward Status</dt><dd>{d.reward.status}</dd></div>
            <div><dt>Wallet Status</dt><dd>{d.reward.walletStatus}</dd></div>
            {d.reward.reason && <div><dt>Note</dt><dd>{d.reward.reason}</dd></div>}
          </dl>

          {d.eligibility?.history?.length > 0 && (
            <>
              <h4 className="ar-section-title">Eligibility History</h4>
              <ul className="ar-history">
                {d.eligibility.history.map((h, i) => (
                  <li key={i}>
                    <strong style={{ color: (ELIGIBILITY_META[h.status] || {}).color }}>{h.status}</strong>
                    <span className="ar-muted">{formatDateTime(h.at)}{h.trigger ? ` · ${h.trigger}` : ""}</span>
                    <span className="ar-elig-detail">{h.summary}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* STEP 24 — the decision. Only offered while there is one to make. */}
          {!d.reward.creditedAt && d.reward.status !== "rejected" && (
            <div className="ar-actions">
              <input
                placeholder="Reason (required to reject)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={300}
              />
              <div className="ar-actions-row">
                {/* The server re-checks every required condition again on
                    approve; this only mirrors its verdict. */}
                <button
                  className="ar-btn ar-btn-good"
                  disabled={busy || !d.approvable}
                  onClick={() => act("approve")}
                  title={d.approvable ? "" : "Every required condition must pass"}
                >
                  Approve &amp; Credit ₹{d.reward.amount}
                </button>
                <button className="ar-btn ar-btn-bad" disabled={busy} onClick={() => act("reject")}>
                  Reject
                </button>
              </div>
              {!d.approvable && (
                <p className="ar-muted">
                  Not payable yet — {d.eligibility?.reason || "not every required condition is satisfied."}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

// =====================================================
// STEP 23 — FRAUD QUEUE
// =====================================================

function FraudTab({ showToast, onChanged }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    adminReferral
      .fraudQueue({ limit: 50 })
      .then(setData)
      .catch(() => showToast("Could not load the fraud queue.", "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(load, [load]);

  if (!data) return <div className="ar-loading">Loading…</div>;
  if (!data.items.length) {
    return <div className="ar-empty">Nothing is waiting for review. 🎉</div>;
  }

  return (
    <>
      <div className="ar-rows">
        {data.items.map((r) => (
          <button key={r.id} className="ar-row ar-row-review" onClick={() => setOpenId(r.id)}>
            <div className="ar-row-main">
              <strong>{r.referredUser?.name || "(deleted)"}</strong>
              <span className="ar-row-arrow">← referred by</span>
              <strong>{r.referrer?.name || "(deleted)"}</strong>
            </div>
            <ul className="ar-row-reasons">
              {r.reasons.map((x, i) => (
                <li key={i} title={x.detail}>⚠️ {x.label}</li>
              ))}
            </ul>
            <div className="ar-row-meta">
              <span className="ar-row-amount">₹{r.amount} held</span>
              <span className="ar-muted">{formatDateTime(r.completedAt)}</span>
            </div>
          </button>
        ))}
      </div>

      {openId && (
        <ReferralDetail
          id={openId}
          onClose={() => setOpenId(null)}
          onActed={() => {
            setOpenId(null);
            load();
            onChanged();
          }}
          showToast={showToast}
        />
      )}
    </>
  );
}

// =====================================================
// STEP 21 / STEP 22 — PAYOUTS
// =====================================================

function PayoutsTab({ showToast, onChanged }) {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    adminReferral
      .payouts({ status, limit: 50 })
      .then(setData)
      .catch(() => showToast("Could not load payouts.", "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(load, [load]);

  return (
    <>
      {/* The four payout states, plus rejected. "Paid" and "Skipped" are both
          completed payouts — Skipped is one that was completed without a
          transaction reference, which is allowed (the reference is optional). */}
      <div className="ar-filters">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="pending">🟡 Pending</option>
          <option value="processing">🔵 Processing</option>
          <option value="paid">🟢 Paid (with UTR)</option>
          <option value="skipped">⚪ Skipped (no UTR)</option>
          <option value="rejected">🔴 Rejected</option>
          <option value="">All</option>
        </select>
      </div>

      {!data ? (
        <div className="ar-loading">Loading…</div>
      ) : !data.items.length ? (
        <div className="ar-empty">No payouts in this state.</div>
      ) : (
        <div className="ar-rows">
          {data.items.map((w) => (
            <button key={w.id} className="ar-row" onClick={() => setOpenId(w.id)}>
              <div className="ar-row-main">
                <strong>{w.user?.name || "(deleted)"}</strong>
                <span className="ar-muted">{w.user?.email}</span>
              </div>
              <div className="ar-row-meta">
                <span className="ar-row-amount">₹{w.amount}</span>
                <span className="ar-muted">{w.accountMasked}</span>
                {w.phone && <span className="ar-muted">📞 {w.phone}</span>}
                <PayoutStatus w={w} />
              </div>
            </button>
          ))}
        </div>
      )}

      {openId && (
        <PayoutDetail
          id={openId}
          onClose={() => setOpenId(null)}
          onActed={() => {
            setOpenId(null);
            load();
            onChanged();
          }}
          showToast={showToast}
        />
      )}
    </>
  );
}

// The payout status, as one clearly labelled chip. `displayStatus` is what the
// server calls it — a completed payout with no transaction reference reads
// "skipped", one with a reference reads "paid" — and it falls back to the
// stored status so an older response still renders.
const PAYOUT_STATUS_META = {
  pending: { label: "Pending", icon: "🟡" },
  processing: { label: "Processing", icon: "🔵" },
  paid: { label: "Paid", icon: "🟢" },
  skipped: { label: "Skipped — no UTR", icon: "⚪" },
  rejected: { label: "Rejected", icon: "🔴" },
};

function PayoutStatus({ w }) {
  const key = w.displayStatus || w.status;
  const meta = PAYOUT_STATUS_META[key] || { label: key, icon: "•" };
  return (
    <span className={`ar-pill ar-pill-${key}`} title={meta.label}>
      {meta.icon} {meta.label}
    </span>
  );
}

// STEP 21 — payout details, and STEP 22 — Pay Reward.
function PayoutDetail({ id, onClose, onActed, showToast }) {
  const [w, setW] = useState(null);
  const [utr, setUtr] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminReferral
      .payoutDetail(id)
      .then(setW)
      .catch(() => showToast("Could not load the payout.", "error"));
  }, [id, showToast]);

  useEffect(load, [load]);

  // `overrides` lets Skip complete the payout with the UTR box deliberately
  // ignored, so a half-typed reference can never be recorded by accident.
  const act = async (action, overrides = {}) => {
    setBusy(true);
    try {
      const res = await adminReferral.payoutAction(id, { action, utr, reason, ...overrides });
      showToast(res.message, "success");
      onActed();
    } catch (err) {
      showToast(err.response?.data?.message || "Could not update the payout.", "error");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value) => {
    try {
      await adminReferral.verifyBank(id, value);
      showToast(`Payout details marked ${value}.`, "success");
      load();
    } catch (err) {
      showToast(err.response?.data?.message || "Could not update the verification.", "error");
    }
  };

  const open = w && (w.status === "pending" || w.status === "processing");

  return (
    <Modal onClose={onClose} title="Payout Details">
      {!w ? (
        <div className="ar-loading">Loading…</div>
      ) : (
        <>
          <dl className="ar-kv">
            <div><dt>User</dt><dd>{w.user?.name} · {w.user?.email}</dd></div>
            <div><dt>User ID</dt><dd className="ar-mono">{w.user?.id}</dd></div>
            <div><dt>Reward Amount</dt><dd><strong>₹{w.amount}</strong></dd></div>
            <div><dt>Wallet Balance</dt><dd>₹{w.wallet.available} available · ₹{w.wallet.locked} locked</dd></div>
            <div><dt>Requested</dt><dd>{formatDateTime(w.requestedAt)}</dd></div>
            <div><dt>Status</dt><dd><PayoutStatus w={w} /></dd></div>
          </dl>

          <h4 className="ar-section-title">Bank Details</h4>
          {!w.bank ? (
            <p className="ar-muted">This user has no payout details on file.</p>
          ) : (
            <>
              <dl className="ar-kv">
                <div><dt>Account Holder</dt><dd>{w.bank.accountHolderName || "—"}</dd></div>
                <div><dt>Bank</dt><dd>{w.bank.bankName || "—"}</dd></div>
                <div><dt>Account Number</dt><dd className="ar-mono">{w.bank.accountNumber || "—"}</dd></div>
                <div><dt>IFSC</dt><dd className="ar-mono">{w.bank.ifsc || "—"}</dd></div>
                {/* The number to ring if this payout has a problem. */}
                <div>
                  <dt>Phone</dt>
                  <dd className="ar-mono">
                    {w.bank.phone ? <a href={`tel:${w.bank.phone}`}>📞 {w.bank.phone}</a> : "—"}
                  </dd>
                </div>
                {/* Only present on accounts saved before the UPI field was
                    replaced by the phone number. */}
                {w.bank.upiId && (
                  <div><dt>UPI ID (legacy)</dt><dd className="ar-mono">{w.bank.upiId}</dd></div>
                )}
                <div><dt>Verification</dt><dd>{w.bank.verificationStatus}</dd></div>
              </dl>
              {/* If the account was edited after the request was made, the
                  snapshot and the live details disagree — say so rather than
                  letting an admin pay the wrong account. */}
              {w.snapshot.accountNumberMasked &&
                w.bank.accountLast4 &&
                w.snapshot.accountNumberMasked !== `••••${w.bank.accountLast4}` && (
                  <p className="ar-warn">
                    ⚠️ These details were changed after the request was made. The request
                    was for {w.snapshot.accountNumberMasked}.
                  </p>
                )}
              <div className="ar-actions-row">
                <button className="ar-btn ar-btn-sm" onClick={() => verify("verified")}>
                  Mark verified
                </button>
                <button className="ar-btn ar-btn-sm" onClick={() => verify("rejected")}>
                  Mark rejected
                </button>
              </div>
            </>
          )}

          {w.previousPayouts.length > 0 && (
            <>
              <h4 className="ar-section-title">Previous Payouts</h4>
              <ul className="ar-history">
                {w.previousPayouts.map((h) => (
                  <li key={h.id}>
                    <span>₹{h.amount}</span>
                    <PayoutStatus w={h} />
                    <span className="ar-muted">{formatDateTime(h.paidAt || h.requestedAt)}</span>
                    {h.utr ? (
                      <span className="ar-mono">{h.utr}</span>
                    ) : h.utrSkipped ? (
                      <span className="ar-muted">no reference</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* STEP 22 — the payment itself.
              TASK 4: the reference is OPTIONAL. Enter the UTR / Transaction ID /
              UPI Transaction ID and complete, or press Skip and complete
              without one. A payout is never blocked for want of a reference. */}
          {open && (
            <div className="ar-actions">
              <label className="ar-field-label" htmlFor="ar-utr">
                UTR / Transaction ID / UPI Transaction ID{" "}
                <span className="ar-optional">— OPTIONAL, you can skip it</span>
              </label>
              <input
                id="ar-utr"
                placeholder="e.g. 402912345678 (leave blank to skip)"
                value={utr}
                onChange={(e) => setUtr(e.target.value)}
                maxLength={64}
              />
              <input
                placeholder="Reason (required to reject)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={300}
              />
              <div className="ar-actions-row">
                {w.status === "pending" && (
                  <button className="ar-btn" disabled={busy} onClick={() => act("process")}>
                    Start Processing
                  </button>
                )}
                <button
                  className="ar-btn ar-btn-good"
                  disabled={busy || !utr.trim()}
                  title={utr.trim() ? "" : "Enter a reference, or use Skip & Complete"}
                  onClick={() => act("pay")}
                >
                  ✅ Complete with UTR · ₹{w.amount}
                </button>
                <button
                  className="ar-btn"
                  disabled={busy}
                  title="Complete this payout without recording a reference"
                  onClick={() => act("pay", { utr: "" })}
                >
                  ⏭ Skip &amp; Complete · ₹{w.amount}
                </button>
                <button className="ar-btn ar-btn-bad" disabled={busy} onClick={() => act("reject")}>
                  Reject
                </button>
              </div>
              <p className="ar-muted ar-hint">
                Both buttons pay the user and mark the payout complete. The only
                difference is whether a transaction reference is recorded against it —
                <strong> Paid</strong> with one, <strong>Skipped</strong> without.
              </p>
            </div>
          )}

          {w.status === "paid" && (
            <p className="ar-pass">
              ✅ Paid {formatDateTime(w.paidAt)} ·{" "}
              {w.utr ? (
                <>
                  UTR <span className="ar-mono">{w.utr}</span>
                </>
              ) : (
                <>no transaction reference recorded (skipped)</>
              )}
            </p>
          )}
          {w.status === "rejected" && <p className="ar-warn">❌ Rejected — {w.reason}</p>}
        </>
      )}
    </Modal>
  );
}

// =====================================================
// SETTINGS — reward, qualifying time, eligibility conditions
//
// Every value here is stored and VALIDATED by the server, and is what the
// backend eligibility check actually enforces — this screen only edits it.
// A condition can only be one of the server's supported types; a type that
// still needs development is listed but cannot be switched on.
// =====================================================

function SettingsTab({ showToast, onChanged }) {
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ rewardAmount: "", requiredUsageMin: "" });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // a condition key, or "new"

  const apply = (v) => {
    setView(v);
    setForm({
      rewardAmount: String(v.settings.rewardAmount),
      requiredUsageMin: String(v.settings.requiredUsageMin),
    });
  };

  const load = useCallback(() => {
    adminReferral
      .settings()
      .then(apply)
      .catch(() => showToast("Could not load the settings.", "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(load, [load]);

  if (!view) return <div className="ar-loading">Loading…</div>;

  const { settings, limits, catalog, history } = view;
  const typeInfo = Object.fromEntries(catalog.map((t) => [t.type, t]));

  const step = (field, delta, min, max) =>
    setForm((f) => {
      const n = Math.round(Number(f[field]) || 0) + delta;
      return { ...f, [field]: String(Math.min(max, Math.max(min, n))) };
    });

  const saveProgramme = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await adminReferral.saveSettings({
        rewardAmount: Number(form.rewardAmount),
        requiredUsageMin: Number(form.requiredUsageMin),
      });
      showToast(res.message, "success");
      load();
      onChanged();
    } catch (err) {
      showToast(err.response?.data?.message || "Could not save the settings.", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (c) => {
    try {
      const res = await adminReferral.updateCondition(c.key, { enabled: !c.enabled });
      showToast(res.message, "success");
      load();
    } catch (err) {
      showToast(err.response?.data?.message || "Could not update the condition.", "error");
    }
  };

  const dirty =
    form.rewardAmount !== String(settings.rewardAmount) ||
    form.requiredUsageMin !== String(settings.requiredUsageMin);

  return (
    <>
      {/* ===== REWARD + QUALIFYING TIME ===== */}
      <section className="ar-panel">
        <h3 className="ar-panel-title">Programme</h3>
        <form className="ar-settings" onSubmit={saveProgramme}>
          <div className="ar-setting">
            <label htmlFor="ar-reward">Reward per eligible referral (₹)</label>
            <div className="ar-stepper">
              <button
                type="button"
                className="ar-btn ar-btn-sm"
                aria-label="Decrease reward"
                onClick={() => step("rewardAmount", -1, limits.rewardMin, limits.rewardMax)}
              >
                −
              </button>
              <input
                id="ar-reward"
                type="number"
                inputMode="numeric"
                min={limits.rewardMin}
                max={limits.rewardMax}
                step={1}
                value={form.rewardAmount}
                onChange={(e) => setForm((f) => ({ ...f, rewardAmount: e.target.value }))}
                required
              />
              <button
                type="button"
                className="ar-btn ar-btn-sm"
                aria-label="Increase reward"
                onClick={() => step("rewardAmount", 1, limits.rewardMin, limits.rewardMax)}
              >
                +
              </button>
            </div>
            <span className="ar-muted">
              Current: ₹{settings.rewardAmount} · allowed ₹{limits.rewardMin}–₹{limits.rewardMax}
            </span>
          </div>

          <div className="ar-setting">
            <label htmlFor="ar-minutes">Qualifying time (minutes of active use)</label>
            <div className="ar-stepper">
              <button
                type="button"
                className="ar-btn ar-btn-sm"
                aria-label="Decrease qualifying time"
                onClick={() => step("requiredUsageMin", -1, limits.usageMinutesMin, limits.usageMinutesMax)}
              >
                −
              </button>
              <input
                id="ar-minutes"
                type="number"
                inputMode="numeric"
                min={limits.usageMinutesMin}
                max={limits.usageMinutesMax}
                step={1}
                value={form.requiredUsageMin}
                onChange={(e) => setForm((f) => ({ ...f, requiredUsageMin: e.target.value }))}
                required
              />
              <button
                type="button"
                className="ar-btn ar-btn-sm"
                aria-label="Increase qualifying time"
                onClick={() => step("requiredUsageMin", 1, limits.usageMinutesMin, limits.usageMinutesMax)}
              >
                +
              </button>
            </div>
            <span className="ar-muted">
              Current: {settings.requiredUsageMin} min · allowed {limits.usageMinutesMin}–{limits.usageMinutesMax} min
            </span>
          </div>

          <div className="ar-actions-row">
            <button className="ar-btn ar-btn-good" type="submit" disabled={saving || !dirty}>
              {saving ? "Saving…" : "Save"}
            </button>
            {dirty && (
              <button type="button" className="ar-btn" onClick={() => apply(view)}>
                Discard
              </button>
            )}
          </div>
          <p className="ar-muted ar-hint">
            A new reward applies to every referral that becomes eligible from now on; rewards
            already earned, approved or paid keep their amount. A new qualifying time applies to
            every referral still running — one that already completed its time keeps it.
          </p>
        </form>
      </section>

      {/* ===== ELIGIBILITY CONDITIONS ===== */}
      <section className="ar-panel">
        <div className="ar-panel-head">
          <h3 className="ar-panel-title">Eligibility Conditions</h3>
          <button className="ar-btn ar-btn-good ar-btn-sm" onClick={() => setEditing("new")}>
            ＋ Add Condition
          </button>
        </div>
        <p className="ar-muted">
          A referral is eligible only when every <strong>active, required</strong> condition
          passes. Anti-fraud and duplicate-reward checks always run on top and cannot be turned
          off.
        </p>

        <ul className="ar-cond-list">
          {settings.conditions.map((c) => {
            const t = typeInfo[c.type] || {};
            return (
              <li key={c.key} className={`ar-cond ${c.enabled ? "" : "is-off"}`}>
                <div className="ar-cond-main">
                  <strong>{c.name}</strong>
                  <span className="ar-cond-tags">
                    <span className={`ar-pill ${c.enabled ? "ar-pill-paid" : "ar-pill-rejected"}`}>
                      {c.enabled ? "Active" : "Disabled"}
                    </span>
                    <span className="ar-pill">{c.required ? "Required" : "Optional"}</span>
                    {c.locked && <span className="ar-pill">🔒 Mandatory</span>}
                    {!c.supported && <span className="ar-pill ar-pill-pending">Not yet available</span>}
                  </span>
                  <span className="ar-muted">
                    {t.label || c.type}
                    {conditionConfigSummary(c, t)}
                    {c.description ? ` — ${c.description}` : ""}
                  </span>
                </div>
                <div className="ar-actions-row">
                  <button className="ar-btn ar-btn-sm" onClick={() => setEditing(c.key)}>
                    Edit
                  </button>
                  {!c.locked && (
                    <button
                      className="ar-btn ar-btn-sm"
                      onClick={() => toggle(c)}
                      disabled={!c.enabled && !c.supported}
                      title={!c.supported ? "This condition type needs development before it can be enforced" : ""}
                    >
                      {c.enabled ? "Disable" : "Enable"}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {/* ===== CHANGE HISTORY ===== */}
      <section className="ar-panel">
        <h3 className="ar-panel-title">Change History</h3>
        {!history.length ? (
          <p className="ar-muted">No changes yet — these are the initial settings.</p>
        ) : (
          <ul className="ar-history">
            {history.map((h, i) => (
              <li key={i}>
                <span>{h.summary}</span>
                <span className="ar-muted">
                  {formatDateTime(h.at)}
                  {h.byName ? ` · ${h.byName}` : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {editing && (
        <ConditionForm
          condition={editing === "new" ? null : settings.conditions.find((c) => c.key === editing)}
          catalog={catalog}
          onClose={() => setEditing(null)}
          onSaved={(res) => {
            showToast(res.message, "success");
            setEditing(null);
            load();
          }}
          showToast={showToast}
        />
      )}
    </>
  );
}

// " · 2 blogs" style summary of a condition's parameters.
function conditionConfigSummary(c, t) {
  const fields = t.fields || [];
  if (!fields.length || !c.config) return "";
  const parts = fields.map((f) => {
    const v = c.config[f.name];
    if (v === undefined) return null;
    if (f.type === "select" || f.type === "multiselect") {
      const labels = Object.fromEntries((f.options || []).map((o) => [o.value, o.label]));
      return (Array.isArray(v) ? v : [v]).map((x) => labels[x] || x).join(", ");
    }
    return `${f.label}: ${v}`;
  });
  const text = parts.filter(Boolean).join(" · ");
  return text ? ` · ${text}` : "";
}

// Add Condition / Edit Condition.
function ConditionForm({ condition, catalog, onClose, onSaved, showToast }) {
  const isNew = !condition;
  const addable = catalog.filter((t) => !t.builtIn);
  const [type, setType] = useState(isNew ? (addable.find((t) => t.supported) || addable[0]).type : condition.type);
  const info = catalog.find((t) => t.type === type) || { fields: [] };
  const defaults = (t) => Object.fromEntries((t.fields || []).map((f) => [f.name, f.default]));

  const [form, setForm] = useState(() => ({
    name: isNew ? "" : condition.name,
    description: isNew ? "" : condition.description,
    required: isNew ? true : condition.required,
    enabled: isNew ? true : condition.enabled,
    config: isNew ? defaults(info) : { ...defaults(info), ...condition.config },
  }));
  const [busy, setBusy] = useState(false);

  const locked = !isNew && condition.locked;
  const builtIn = !isNew && condition.builtIn;
  const canEnable = info.supported;

  const changeType = (next) => {
    setType(next);
    const t = catalog.find((x) => x.type === next) || {};
    setForm((f) => ({ ...f, config: defaults(t), enabled: f.enabled && !!t.supported }));
  };

  const setConfig = (name, value) => setForm((f) => ({ ...f, config: { ...f.config, [name]: value } }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        ...(locked ? {} : { required: form.required, enabled: form.enabled && canEnable }),
        ...(builtIn ? {} : { config: form.config }),
      };
      const res = isNew
        ? await adminReferral.addCondition({ type, ...payload })
        : await adminReferral.updateCondition(condition.key, payload);
      onSaved(res);
    } catch (err) {
      showToast(err.response?.data?.message || "Could not save the condition.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} title={isNew ? "Add Condition" : `Edit — ${condition.name}`}>
      <form className="ar-cond-form" onSubmit={submit}>
        <label className="ar-field">
          <span>Condition type</span>
          {isNew ? (
            <select value={type} onChange={(e) => changeType(e.target.value)}>
              {addable.map((t) => (
                <option key={t.type} value={t.type}>
                  {t.label}
                  {t.supported ? "" : " — not yet available"}
                </option>
              ))}
            </select>
          ) : (
            <input value={info.label || type} disabled />
          )}
          <span className="ar-muted">{info.description}</span>
        </label>

        {!canEnable && (
          <p className="ar-warn">
            ⚠️ This condition type is not yet available — it needs development work (a backend
            evaluator) before it can be enforced. It can be saved as a disabled draft only.
          </p>
        )}

        <label className="ar-field">
          <span>Condition name</span>
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder={isNew ? "e.g. Complete your profile" : ""}
            maxLength={80}
            required={!isNew}
          />
        </label>

        <label className="ar-field">
          <span>Description / instructions shown to users</span>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            maxLength={300}
            rows={2}
          />
        </label>

        {/* Type-specific parameters — exactly the fields the server's
            evaluator for this type reads. */}
        {!builtIn &&
          info.fields.map((f) => (
            <label className="ar-field" key={f.name}>
              <span>{f.label}</span>
              {f.type === "number" && (
                <input
                  type="number"
                  inputMode="numeric"
                  min={f.min}
                  max={f.max}
                  step={1}
                  value={form.config[f.name] ?? ""}
                  onChange={(e) => setConfig(f.name, e.target.value === "" ? "" : Number(e.target.value))}
                  required
                />
              )}
              {f.type === "select" && (
                <select value={form.config[f.name] ?? ""} onChange={(e) => setConfig(f.name, e.target.value)}>
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}
              {f.type === "multiselect" && (
                <span className="ar-checks">
                  {f.options.map((o) => {
                    const list = form.config[f.name] || [];
                    return (
                      <label key={o.value} className="ar-check">
                        <input
                          type="checkbox"
                          checked={list.includes(o.value)}
                          onChange={(e) =>
                            setConfig(
                              f.name,
                              e.target.checked ? [...list, o.value] : list.filter((x) => x !== o.value)
                            )
                          }
                        />
                        {o.label}
                      </label>
                    );
                  })}
                </span>
              )}
            </label>
          ))}

        {locked ? (
          <p className="ar-muted">🔒 This is a mandatory programme condition — always active and required.</p>
        ) : (
          <div className="ar-checks">
            <label className="ar-check">
              <input
                type="checkbox"
                checked={form.required}
                onChange={(e) => setForm((f) => ({ ...f, required: e.target.checked }))}
              />
              Required (blocks the reward until it passes)
            </label>
            <label className="ar-check">
              <input
                type="checkbox"
                checked={form.enabled && canEnable}
                disabled={!canEnable}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
              Active
            </label>
          </div>
        )}

        <div className="ar-actions-row">
          <button className="ar-btn ar-btn-good" type="submit" disabled={busy}>
            {busy ? "Saving…" : isNew ? "Add Condition" : "Save"}
          </button>
          <button type="button" className="ar-btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

// =====================================================
// SHARED BITS
// =====================================================

function Modal({ title, children, onClose }) {
  // Escape closes, matching every other dismissible surface in the app.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="ar-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="ar-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ar-modal-head">
          <h3>{title}</h3>
          <button className="ar-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="ar-modal-body">{children}</div>
      </div>
    </div>
  );
}

function Pager({ page, totalPages, onPage }) {
  return (
    <div className="ar-pager">
      <button className="ar-btn ar-btn-sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ← Prev
      </button>
      <span>Page {page} of {totalPages}</span>
      <button
        className="ar-btn ar-btn-sm"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
      >
        Next →
      </button>
    </div>
  );
}

export default AdminReferEarn;
